import axiosInstance from '@/shared/api/axios';
import type {
  ApiPackagesGetItem,
  ApiPackagesListParams,
  ApiPackagesListResponse,
  ApiPackagesParcelPickerParams,
  ApiPackagesParcelPickerResponse,
  ApiPackagesPatchBody,
  ApiPackagesPostBody,
} from '@/shared/types/api/packages';

export const apiPackagesListGet = async (
  params: ApiPackagesListParams,
): Promise<ApiPackagesListResponse> => {
  const { data } = await axiosInstance.get<ApiPackagesListResponse>('/packages', { params });
  return data;
};

/**
 * Fetch every package matching `search` across all pages. The list endpoint has
 * no server-side "unattached" filter, so the party "add package" picker filters
 * `party_id === null` client-side — without fetching every page it would miss
 * any unattached package beyond the first page. Bounded by a page guard.
 */
export const apiPackagesListAll = async (
  params: Omit<ApiPackagesListParams, 'page' | 'page_size'>,
): Promise<ApiPackagesGetItem[]> => {
  const pageSize = 200;
  const all: ApiPackagesGetItem[] = [];
  let page = 1;
  for (let guard = 0; guard < 100; guard += 1) {
    const res = await apiPackagesListGet({ ...params, page, page_size: pageSize });
    all.push(...res.results);
    if (!res.next || res.results.length === 0) break;
    page += 1;
  }
  return all;
};

export const apiPackagesDetailGet = async (id: string): Promise<ApiPackagesGetItem> => {
  const { data } = await axiosInstance.get<ApiPackagesGetItem>(`/packages/${id}`);
  return data;
};

export const apiPackagesPost = async (body: ApiPackagesPostBody): Promise<ApiPackagesGetItem> => {
  const { data } = await axiosInstance.post<ApiPackagesGetItem>('/packages', body);
  return data;
};

export const apiPackagesPatch = async (
  id: string,
  body: ApiPackagesPatchBody,
): Promise<ApiPackagesGetItem> => {
  const { data } = await axiosInstance.patch<ApiPackagesGetItem>(`/packages/${id}`, body);
  return data;
};

export const apiPackagesDelete = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/packages/${id}`);
};

export const apiPackagesParcelPickerGet = async (
  params: ApiPackagesParcelPickerParams,
): Promise<ApiPackagesParcelPickerResponse> => {
  const { data } = await axiosInstance.get<ApiPackagesParcelPickerResponse>(
    '/packages/parcel-picker',
    { params },
  );
  return data;
};

export const apiPackagesNextName = async (): Promise<{ name: string }> => {
  const { data } = await axiosInstance.post<{ name: string }>('/packages/next-name');
  return data;
};
